import React from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage, MessageDescriptor } from 'react-intl';

import PaymentStatus from 'modules/onlinePayment/types/PaymentStatus';
import { FinalInvoice } from 'modules/selfServices/types/SelfService';

import { roundAmountByCountry } from 'utils/priceUtils';
import { useProceedToPaymentMutation } from 'modules/selfServices/service';

import { closePayingModal, openPayingModal } from 'modules/onlinePayment/actions';

import { isPublicDevice } from 'modules/dealers/selectors';
import { isInstructionModalDisplayed } from 'modules/sockets/selectors';
import { getCountry, isInvoicePaymentDelayable } from 'modules/selfServices/selectors';
import { getStatus, isPayingModalOpen as isPayingModalOpenSelector } from 'modules/onlinePayment/selectors';

import Icon from 'components/ui/Icon';
import Switch from 'components/ui/Switch';
import Attachments from 'components/ui/Attachments';
import Button, { ButtonType } from 'components/ui/Button';

import PayingModal from './Modal/PayingModal';
import PendingModal from './Modal/PendingModal';
import KioskInstructionModal from './Modal/KioskInstructionModal';
import { usePriceFormatter } from 'utils/PriceFormatter/usePriceFormatter';
import WarningMessage from './component/WarningMessage';

interface FinalInvoiceCardProps {
  onSkip?: () => void;
  selfServiceId: string;
  finalInvoice: FinalInvoice;
}

const FinalInvoiceCard: React.FC<FinalInvoiceCardProps> = ({
  onSkip,
  finalInvoice,
  selfServiceId,
}) => {
  const dispatch = useDispatch();
  const priceFormatter = usePriceFormatter();

  const [proceedToPayment, { isLoading: isLoadingPayment }] = useProceedToPaymentMutation();

  const country = useSelector(getCountry);
  const isPublic = useSelector(isPublicDevice);
  const paymentStatus = useSelector(getStatus);
  const canDelayPayment = useSelector(isInvoicePaymentDelayable);
  const isPayingModalOpen = useSelector(isPayingModalOpenSelector);
  const isInstructionOpen = useSelector(isInstructionModalDisplayed);

  const [isSkipping, setIsSkipping] = React.useState(false);
  const [isPendingOpen, setPendingOpen] = React.useState(false);
  const [isRedirecting, setIsRedirecting] = React.useState(false);
  const [isPendingConfirmed, setIsPendingConfirmed] = React.useState(false);

  const isLoading = isLoadingPayment || isRedirecting;
  const isPaid = paymentStatus === PaymentStatus.SUCCESS;
  const isPending = paymentStatus === PaymentStatus.PENDING;
  const isPreAuthorized = paymentStatus === PaymentStatus.PRE_AUTHORIZED;
  // Kiosks pay without the pending modal: the confirmation is asked in the card instead.
  const requiresPendingConfirmation = isPublic && isPending;

  // A confirmation given for one pending payment must not carry over to the next one: it is reset
  // as soon as the payment is no longer pending (adjusted during render rather than in an effect).
  const [wasPending, setWasPending] = React.useState(isPending);
  if (isPending !== wasPending) {
    setWasPending(isPending);
    if (!isPending) {
      setIsPendingConfirmed(false);
    }
  }
  const isDisabled = isLoading || isSkipping || isRedirecting;
  const canPay = finalInvoice.payable && !isPaid;

  // The invoice price is nullable in the API
  const price = React.useMemo(
    () => (typeof finalInvoice.price === 'number' ? roundAmountByCountry[country?.code](finalInvoice.price) : undefined),
    [country?.code, finalInvoice.price],
  );

  const handleCancel = React.useCallback(() => setPendingOpen(false), []);
  const handleClosePayingModal = React.useCallback(() => dispatch(closePayingModal()), [dispatch]);

  const handlePaymentOnClick = React.useCallback(() => {
    if (finalInvoice.link) {
      setIsRedirecting(true);
      setPendingOpen(true);
      window.location.assign(finalInvoice.link);
    } else {
      proceedToPayment({ id: selfServiceId });
      setPendingOpen(false);
    }
  }, [finalInvoice.link, proceedToPayment, selfServiceId]);

  const handlePay = React.useCallback(() => {
    if (isPublic) {
      // The confirmation only covers this payment attempt.
      setIsPendingConfirmed(false);
      // Online payment
      if (finalInvoice.link) {
        // While the payment is still pending, this opens the "Payment in progress" step, not a new
        // QR code. The customer only reaches a new QR code after cancelling that step, which
        // dismisses the pending state (`isPendingDismissed`) without asking for a confirmation:
        // accepted for BMWVID-29945. The confirmation fully gates a second payment on terminals only.
        dispatch(openPayingModal());
      } else { // Terminal payment
        proceedToPayment({ id: selfServiceId });
      }
    } else {
      // Bezahl
      // eslint-disable-next-line no-lonely-if
      if (isPending) {
        setPendingOpen(true);
      } else {
        handlePaymentOnClick();
      }
    }
  }, [dispatch, finalInvoice.link, isPending, isPublic, proceedToPayment, selfServiceId, handlePaymentOnClick]);

  const handleOnSkip = React.useCallback(() => {
    if (onSkip) {
      setIsSkipping(true);
      onSkip();
    }
  }, [onSkip]);

  const paymentButtonText: MessageDescriptor = React.useMemo(() => (
    canDelayPayment
      ? { id: 'page.finalInvoice.buttons.paymentNow', defaultMessage: 'Pay now' }
      : { id: 'page.finalInvoice.modal.kioskInstruction.proceedTitle', defaultMessage: 'Proceed with payment' }
  ), [canDelayPayment]);

  const amountLabel = React.useMemo(() => {
    if (isPaid) {
      return { id: 'page.finalInvoice.amountPaid', defaultMessage: 'Amount paid (incl. tax)' };
    }

    return finalInvoice.payable
      ? { id: 'page.finalInvoice.amountToPaid', defaultMessage: 'Amount to be paid (incl. tax)' }
      : { id: 'page.finalInvoice.invoiceAmount', defaultMessage: 'Invoice amount (incl. tax)' };
  }, [isPaid, finalInvoice.payable]);

  const downloadButtonProps = React.useMemo(() => ({
    loading: isLoading,
    disabled: isPendingOpen || isInstructionOpen,
  }), [isLoading, isInstructionOpen, isPendingOpen]);

  const pendingWarning = (
    <WarningMessage
      testId="pending-payment-warning"
      content={
        <FormattedMessage
          id="page.finalInvoice.modal.pending.content"
          defaultMessage="A payment is pending. Be aware that processing time to validate the payment can take some time. Proceed to payment a second time only if you are sure that your payment has been rejected."
        />
      }
    />
  );

  return (
    <div
      className="rounded-card w-full border bg-card-bg border-border-default px-5 py-10 kioskSharebox:px-7 kioskSharebox:py-14 kiosk:px-10 kiosk:py-20"
    >
      <div className="flex items-center w-full flex-col">
        <span
          className={classNames('bg-white rounded-full p-5 kiosk:p-10 kioskSharebox:p-7 flex items-center justify-center', {
            'bg-success-lowest!': isPaid,
            'bg-warning-lowest!': isPreAuthorized
          })}
        >
          <div className={classNames('size-14 rounded-full kiosk:size-28  grid place-items-center',
            { 'bg-success-default': isPaid,
              'bg-warning-default': isPreAuthorized
            })}>
            <Icon className={
              classNames('text-4xl kiosk:text-6xl text-default',
                {
                  'fb-icon--check text-white': isPaid,
                  'fb-icon--cash-banknote-invoice': !isPaid && !isPreAuthorized,
                  'fb-icon--invoice-pre-authorized text-white': isPreAuthorized,
                }
              )}
            />
          </div>
        </span>

        <div className="mt-4 kioskSharebox:mt-8 kiosk:mt-12 font-medium" data-testid="status">
          <FormattedMessage {...amountLabel} tagName="p" />
        </div>

        <div className="mt-3 font-extrabold text-4xl kioskSharebox:mt-4 kiosk:mt-5 kiosk:font-normal kiosk:text-5xl" data-testid="price">
          {priceFormatter(price)}
        </div>
      </div>

      <div className="mt-6 kioskSharebox:mt-8 kiosk:mt-12">
        {canPay && (
          <>
            {/* The customer reads the warning (and confirms it on a kiosk) before reaching the pay buttons. */}
            {isPending && (
              <div className="mb-4 kiosk:mb-8">
                {pendingWarning}
                {requiresPendingConfirmation && (
                  <div className="w-full mt-4 kiosk:mt-8">
                    <Switch
                      data-testid="pending-payment-confirm"
                      checked={isPendingConfirmed}
                      onChange={setIsPendingConfirmed}
                      labelClassName="[&>span]:[font-size:inherit]"
                    >
                      <FormattedMessage
                        id="page.finalInvoice.modal.pending.confirm"
                        defaultMessage="I confirm that I understand I may be making a second payment"
                      />
                    </Switch>
                  </div>
                )}
              </div>
            )}
            <Button
              analytics={{
                category: 'Payment',
                action: 'Payment started',
              }}
              type={ButtonType.TERTIARY}
              className="w-full py-3 kiosk:py-6! text-button-xs!"
              loading={isLoading}
              onClick={handlePay}
              testId="pay-button"
              disabled={isDisabled || (requiresPendingConfirmation && !isPendingConfirmed)}
            >
              <FormattedMessage {...paymentButtonText} />
            </Button>
            {canDelayPayment && (
              <Button
                analytics={{
                  category: 'Payment',
                  action: 'Payment delayed',
                }}
                type={ButtonType.PRIMARY}
                className="w-full mt-3 py-3 kiosk:mt-6 kiosk:py-6! text-button-xs!"
                loading={isLoading || isSkipping}
                onClick={handleOnSkip}
                testId="pay-later-button"
                disabled={isDisabled}
              >
                <FormattedMessage id="page.finalInvoice.buttons.paymentLater" defaultMessage="Pay at the dealership" />
              </Button>
            )}
          </>
        )}
        {isPreAuthorized && (
          <WarningMessage
            testId="pending-payment-preAuthorized"
            content={
              <FormattedMessage
                id="page.finalInvoice.modal.preAuthorized.content"
                defaultMessage="The payment will be debited from your account within the best delay" />}
          />)}
      </div>

      <div className="border-t my-6 kiosk:my-12 kioskSharebox:my-8 border-border-default" />

      <div className="my-6 kiosk:my-8 flex justify-between px-4 text-base text-low">
        <span className="mr-4">
          <FormattedMessage id="page.finalInvoice.invoiceNumber" defaultMessage="Invoice N°" />
        </span>
        <span className="font-bold text-default break-word" data-testid="reference">
          {finalInvoice.name}
        </span>
      </div>
      <div className="kiosk:mx-4">
        <Attachments
          documents={finalInvoice.medias}
          documentClassName={classNames({ 'first:border-0 first:pt-0 last:pb-0': !isPaid })}
          singleDownloadTitle={
            <FormattedMessage id="page.finalInvoice.buttons.download" defaultMessage="Download invoice" />
          }
          buttonProps={downloadButtonProps}
        />
      </div>

      <PendingModal
        loading={isLoading}
        open={isPendingOpen}
        onCancel={handleCancel}
        onClick={handlePaymentOnClick}
        buttonText={paymentButtonText}
      />

      <PayingModal
        open={isPayingModalOpen}
        link={finalInvoice.link}
        selfServiceId={selfServiceId}
        paymentStatus={paymentStatus}
        onCancel={handleClosePayingModal}
      />

      <KioskInstructionModal open={isInstructionOpen} isPaid={isPaid} />
    </div>
  );
};

export default FinalInvoiceCard;
